/**
 * Partners — "The Survey Sheet".
 *
 * The ledger and the plate are one document. This module owns the join:
 *
 *   · search        — typeahead over city, store name and postcode
 *   · filter        — the ledger keeps only matching lines
 *   · selection     — a row marks its pin, a pin marks its row
 *   · states        — empty, loading, plate unavailable
 *
 * The map itself lives in mapbox-map.js (`strichwerke.Mapbox`). This module
 * never touches Mapbox internals beyond the small public surface it exposes:
 * `init`, `fitToCity`, `fitToCountry`, `focusStore`, `clearFocus`.
 */

strichwerke.Partners = {
  root: null,
  input: null,
  suggestions: null,
  list: null,
  cards: [],
  detail: null,
  partners: [],
  cities: [],
  activeIndex: -1,
  selectedName: null,
  query: "",

  init() {
    this.root = document.querySelector("[data-store-locator]");
    if (!this.root) return;

    // The theme editor hot-reloads sections; initialising twice would build a
    // second map and leave the first one half-removed.
    if (this.root.dataset.initialised === "true") return;
    this.root.dataset.initialised = "true";

    this.input = this.root.querySelector("[data-search-input]");
    this.suggestions = this.root.querySelector("[data-suggestions]");
    this.list = this.root.querySelector("[data-store-list]");
    this.cards = Array.from(this.root.querySelectorAll("[data-store-card]"));
    this.detail = this.root.querySelector("[data-store-detail]");
    this.resultsCount = this.root.querySelector("[data-results-count]");
    this.emptyState = this.root.querySelector("[data-empty-state]");
    this.plateEmpty = this.root.querySelector("[data-plate-empty]");
    this.plateEmptyLabel = this.root.querySelector("[data-plate-empty-label]");
    this.plateLoading = this.root.querySelector("[data-plate-loading]");
    this.plateFailed = this.root.querySelector("[data-plate-failed]");

    this.partners = this.readPartners();
    this.cities = this.buildCities();

    this.bindSearch();
    this.bindCards();
    this.bindDetail();
    this.bindReset();
    this.bindRetry();

    this.initMap();
  },

  /* ── Data ─────────────────────────────────────────────────────────── */

  readPartners() {
    const el = this.root.querySelector("[data-partners]");
    if (!el) return [];
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      console.error("Partners: could not parse partner data.", error);
      return [];
    }
  },

  /** City → number of shops, sorted by count then name. */
  buildCities() {
    const counts = new Map();
    this.partners.forEach((partner) => {
      const city = (partner.city || "").trim();
      if (!city) return;
      counts.set(city, (counts.get(city) || 0) + 1);
    });
    return Array.from(counts, ([city, count]) => ({ city, count })).sort(
      (a, b) => b.count - a.count || a.city.localeCompare(b.city, "de")
    );
  },

  /* ── Search ───────────────────────────────────────────────────────── */

  bindSearch() {
    if (!this.input) return;

    this.input.addEventListener("input", () => this.onInput());
    this.input.addEventListener("focus", () => {
      if (this.input.value.trim()) this.onInput();
    });
    this.input.addEventListener("keydown", (event) => this.onKeydown(event));

    document.addEventListener("click", (event) => {
      if (!this.root.querySelector("[data-search]").contains(event.target)) {
        this.closeSuggestions();
      }
    });
  },

  onInput() {
    this.query = this.input.value.trim();
    const matches = this.matchCities(this.query);

    this.renderSuggestions(matches);
    this.applyFilter(this.query);
  },

  /** Cities whose name, a store name, or a postcode matches the query. */
  matchCities(query) {
    if (!query) return this.cities.slice(0, 6);
    const needle = query.toLowerCase();

    const cityHits = this.cities.filter((entry) =>
      entry.city.toLowerCase().includes(needle)
    );
    if (cityHits.length) return cityHits.slice(0, 6);

    // Fall back to store names and postcodes, then report the city.
    const citySet = new Set();
    this.partners.forEach((partner) => {
      const haystack = `${partner.name || ""} ${partner.addresses || ""}`.toLowerCase();
      if (haystack.includes(needle)) citySet.add((partner.city || "").trim());
    });

    return this.cities
      .filter((entry) => citySet.has(entry.city))
      .slice(0, 6);
  },

  renderSuggestions(matches) {
    if (!this.suggestions) return;

    if (!matches.length) {
      this.closeSuggestions();
      return;
    }

    this.suggestions.innerHTML = matches
      .map(
        (entry, index) => `
        <li
          class="store-locator__suggestion"
          role="option"
          id="store-suggestion-${index}"
          data-suggestion
          data-city="${this.escape(entry.city)}"
          aria-selected="${index === 0}"
        >
          <span>${this.escape(entry.city)}</span>
          <span class="store-locator__suggestion-count">${String(entry.count).padStart(2, "0")}</span>
        </li>`
      )
      .join("");

    this.activeIndex = 0;
    this.suggestions.hidden = false;
    this.input.setAttribute("aria-expanded", "true");
    this.input.setAttribute("aria-activedescendant", "store-suggestion-0");

    this.suggestions.querySelectorAll("[data-suggestion]").forEach((item) => {
      item.addEventListener("mousedown", (event) => {
        event.preventDefault();
        this.selectCity(item.dataset.city);
      });
    });
  },

  closeSuggestions() {
    if (!this.suggestions) return;
    this.suggestions.hidden = true;
    this.suggestions.innerHTML = "";
    this.activeIndex = -1;
    this.input.setAttribute("aria-expanded", "false");
    this.input.removeAttribute("aria-activedescendant");
  },

  onKeydown(event) {
    const items = this.suggestions
      ? Array.from(this.suggestions.querySelectorAll("[data-suggestion]"))
      : [];

    if (event.key === "Escape") {
      this.closeSuggestions();
      return;
    }

    if (!items.length) {
      if (event.key === "Enter") {
        event.preventDefault();
        this.applyFilter(this.input.value.trim());
      }
      return;
    }

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      this.activeIndex = (this.activeIndex + step + items.length) % items.length;
      items.forEach((item, index) => {
        item.setAttribute("aria-selected", String(index === this.activeIndex));
      });
      this.input.setAttribute(
        "aria-activedescendant",
        `store-suggestion-${this.activeIndex}`
      );
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      const active = items[this.activeIndex] || items[0];
      if (active) this.selectCity(active.dataset.city);
    }
  },

  selectCity(city) {
    this.input.value = city;
    this.query = city;
    this.closeSuggestions();
    this.applyFilter(city);
    this.focusCity(city);
  },

  /* ── Filtering ────────────────────────────────────────────────────── */

  applyFilter(query) {
    const needle = (query || "").toLowerCase();
    let visible = 0;

    this.cards.forEach((card) => {
      const haystack = `${card.dataset.name} ${card.dataset.city} ${card.dataset.address}`.toLowerCase();
      const match = !needle || haystack.includes(needle);
      card.hidden = !match;
      if (match) visible += 1;
    });

    const isEmpty = visible === 0;
    this.root.dataset.empty = String(isEmpty);

    if (this.resultsCount) {
      const template =
        this.root.dataset.countTemplate || "__COUNT__ / __TOTAL__";
      this.resultsCount.textContent = template
        .replace("__COUNT__", String(visible).padStart(2, "0"))
        .replace("__TOTAL__", String(this.cards.length));
    }

    if (this.emptyState) this.emptyState.hidden = !isEmpty;
    if (this.list) this.list.hidden = isEmpty;

    if (this.plateEmpty) {
      this.plateEmpty.hidden = !isEmpty;
      if (isEmpty && this.plateEmptyLabel) {
        const template =
          this.root.dataset.emptyLabelTemplate || "__QUERY__";
        this.plateEmptyLabel.textContent = template.replace(
          "__QUERY__",
          query.toUpperCase()
        );
      }
    }

    if (isEmpty) {
      this.clearSelection();
    }
  },

  /* ── Selection ────────────────────────────────────────────────────── */

  bindCards() {
    this.cards.forEach((card) => {
      card.addEventListener("click", () => this.selectStore(card));
      card.addEventListener("mouseenter", () => {
        if (strichwerke.Mapbox && strichwerke.Mapbox.highlightStore) {
          strichwerke.Mapbox.highlightStore(card.dataset.name);
        }
      });
      card.addEventListener("mouseleave", () => {
        if (strichwerke.Mapbox && strichwerke.Mapbox.highlightStore) {
          strichwerke.Mapbox.highlightStore(null);
        }
      });
    });
  },

  selectStore(card) {
    this.cards.forEach((item) => {
      item.setAttribute("aria-current", String(item === card));
    });
    this.selectedName = card.dataset.name;

    this.showDetail(card);

    // Fly the plate to this store. `flyToStore` also marks the pin, so the
    // ledger and the map share one selection.
    if (strichwerke.Mapbox && strichwerke.Mapbox.flyToStore) {
      strichwerke.Mapbox.flyToStore(card.dataset.name);
    } else if (strichwerke.Mapbox && strichwerke.Mapbox.focusStore) {
      strichwerke.Mapbox.focusStore(card.dataset.name);
    }
  },

  /** Called by the plate when a pin is clicked, so the row follows the pin. */
  selectStoreByName(name) {
    const card = this.cards.find((item) => item.dataset.name === name);
    if (!card) return;

    if (card.hidden) {
      this.input.value = card.dataset.city;
      this.query = card.dataset.city;
      this.applyFilter(card.dataset.city);
    }

    this.selectStore(card);
    card.scrollIntoView({ block: "nearest" });
  },

  clearSelection() {
    this.cards.forEach((item) => item.setAttribute("aria-current", "false"));
    this.selectedName = null;
    if (this.detail) this.detail.hidden = true;
    if (strichwerke.Mapbox && strichwerke.Mapbox.clearFocus) {
      strichwerke.Mapbox.clearFocus();
    }
  },

  showDetail(card) {
    if (!this.detail) return;

    const name = card.dataset.name || "";
    const city = card.dataset.city || "";
    const address = card.dataset.address || "";
    const website = card.dataset.website || "";

    this.detail.querySelector("[data-detail-eyebrow]").textContent = city;
    this.detail.querySelector("[data-detail-name]").textContent = name;
    this.detail.querySelector("[data-detail-address]").textContent = address;
    this.detail.querySelector("[data-detail-city]").textContent = city;
    this.detail.querySelector("[data-detail-website]").textContent = website
      ? website.replace(/^https?:\/\//, "").replace(/\/$/, "")
      : "—";

    const link = this.detail.querySelector("[data-detail-link]");
    if (website) {
      link.href = website;
      link.hidden = false;
    } else {
      link.hidden = true;
    }

    this.detail.hidden = false;
  },

  bindDetail() {
    const close = this.root.querySelector("[data-detail-close]");
    if (close) close.addEventListener("click", () => this.clearSelection());
  },

  /* ── Reset / retry ────────────────────────────────────────────────── */

  bindReset() {
    this.root.querySelectorAll("[data-reset-search]").forEach((button) => {
      button.addEventListener("click", () => {
        this.input.value = "";
        this.query = "";
        this.closeSuggestions();
        this.applyFilter("");
        this.focusCountry();
        this.input.focus();
      });
    });
  },

  bindRetry() {
    const retry = this.root.querySelector("[data-retry-map]");
    if (retry) retry.addEventListener("click", () => this.initMap(true));
  },

  /* ── Map ──────────────────────────────────────────────────────────── */

  initMap(force) {
    if (!window.strichwerke || !strichwerke.Mapbox) return;
    if (!document.getElementById("mapbox-map")) return;

    if (this.plateLoading) this.plateLoading.hidden = false;
    if (this.plateFailed) this.plateFailed.hidden = true;

    const started = strichwerke.Mapbox.init(
      "mapbox-map",
      undefined,
      { pitch: 0, bearing: 0, zoom: 6, center: [10.0, 51.0] },
      {
        onReady: () => {
          if (this.plateLoading) this.plateLoading.hidden = true;
          if (this.plateFailed) this.plateFailed.hidden = true;
        },
        onError: () => {
          if (this.plateLoading) this.plateLoading.hidden = true;
          if (this.plateFailed) this.plateFailed.hidden = false;
        },
      }
    );

    if (started === false && this.plateLoading) {
      this.plateLoading.hidden = true;
      if (this.plateFailed) this.plateFailed.hidden = false;
    }

    if (force) this.applyFilter(this.query);
  },

  focusCity(city) {
    if (strichwerke.Mapbox && strichwerke.Mapbox.fitToCity) {
      strichwerke.Mapbox.fitToCity(city);
    }
  },

  focusCountry() {
    if (strichwerke.Mapbox && strichwerke.Mapbox.fitToCountry) {
      strichwerke.Mapbox.fitToCountry();
    }
  },

  /* ── Utilities ────────────────────────────────────────────────────── */

  escape(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  },
};
